import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiCalendar, FiCheck, FiClock, FiUserCheck } = FiIcons

const statusOptions = ['Present', 'Absent', 'Sick', 'Holiday', 'Field Trip']

const Attendance = () => {
  const [selectedStudent, setSelectedStudent] = useState('Ava M.')
  const [selectedMonth, setSelectedMonth] = useState('2024-09')
  const [dailyRecords, setDailyRecords] = useState({
    '2024-09-01': 'Holiday',
    '2024-09-02': 'Present',
    '2024-09-03': 'Present',
    '2024-09-04': 'Field Trip',
    '2024-09-05': 'Sick',
  })
  const [todayStatus, setTodayStatus] = useState('Present')

  const daysInMonth = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number)
    return new Date(year, month, 0).getDate()
  }, [selectedMonth])

  const handleStatusChange = (dateKey, status) => {
    setDailyRecords(prev => ({ ...prev, [dateKey]: status }))
  }

  const totals = useMemo(() => {
    return statusOptions.reduce((acc, status) => {
      acc[status] = Object.values(dailyRecords).filter(val => val === status).length
      return acc
    }, {})
  }, [dailyRecords])

  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1)
  const [year, month] = selectedMonth.split('-')

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm uppercase tracking-wider text-blue-100">Records</p>
            <h1 className="text-3xl font-bold">Attendance Tracker</h1>
            <p className="text-blue-100 mt-2">Record daily attendance and export monthly reports.</p>
          </div>
          <SafeIcon icon={FiCalendar} className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Student</p>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              >
                <option value="Ava M.">Ava M.</option>
                <option value="Liam J.">Liam J.</option>
                <option value="Sofia R.">Sofia R.</option>
              </select>
            </div>
            <div>
              <p className="text-sm text-gray-600">Month</p>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              />
            </div>
            <div>
              <p className="text-sm text-gray-600">Today&apos;s Status</p>
              <div className="flex items-center space-x-2 mt-1">
                <select
                  value={todayStatus}
                  onChange={(e) => setTodayStatus(e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                >
                  {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
                <button
                  onClick={() => {
                    const today = new Date()
                    const dateKey = today.toISOString().split('T')[0]
                    handleStatusChange(dateKey, todayStatus)
                  }}
                  className="inline-flex items-center px-3 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088cc]"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4 mr-1" />
                  Save
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-800">Monthly Calendar</h3>
              <span className="text-sm text-gray-500 flex items-center">
                <SafeIcon icon={FiClock} className="w-4 h-4 mr-1" />
                Totals update instantly
              </span>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map(day => {
                const dayString = day.toString().padStart(2, '0')
                const dateKey = `${year}-${month}-${dayString}`
                const status = dailyRecords[dateKey] || 'Present'
                return (
                  <div key={dateKey} className="p-2 border border-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Day {day}</p>
                    <select
                      value={status}
                      onChange={(e) => handleStatusChange(dateKey, e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                    >
                      {statusOptions.map(option => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <SafeIcon icon={FiUserCheck} className="w-5 h-5 mr-2 text-[#00AAFF]" />
              Attendance Report
            </h3>
            <span className="text-sm text-gray-500">{selectedStudent}</span>
          </div>
          <div className="space-y-3">
            {statusOptions.map(status => (
              <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">{status}</span>
                <span className="font-semibold text-[#1B4C87]">{totals[status]}</span>
              </div>
            ))}
          </div>
          <div className="p-4 bg-blue-50 rounded-lg text-sm text-[#1B4C87]">
            Monthly attendance summaries include totals for Present, Absent, Sick, Holiday, and Field Trip days.
          </div>
        </div>
      </div>
    </div>
  )
}

export default Attendance
